import React, { useState, useEffect } from 'react';
import { saasData, supabase } from '../../lib/supabase';
import { TeacherDirectoryView } from './TeacherDirectoryView';
import { TeacherDetailModal } from './TeacherDetailModal';
import ComprehensiveClassroomDashboard from './ComprehensiveClassroomDashboard';
import ClassDetailPage from './ClassDetailPage';
import { SubjectTeachersView } from './SubjectTeachersView';
import { useTeacherData } from './hooks/useTeacherData';
import './TeacherDirectoryModule.css';

// Teacher Directory Module Component
const TeacherDirectoryModule = ({ onBack, onTabChange, onSidebarClose }) => {
  const [selectedView, setSelectedView] = useState('directory'); // directory, classroom-management, subject-teachers
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  
  // Teacher management states
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [selectedClassDetail, setSelectedClassDetail] = useState(null);
  const [selectedSubjectDetail, setSelectedSubjectDetail] = useState(null);
  const [selectedECADetail, setSelectedECADetail] = useState(null);
  const [selectedCommitteeDetail, setSelectedCommitteeDetail] = useState(null);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  const [showStudentDetailModal, setShowStudentDetailModal] = useState(false);
  const [teacherView, setTeacherView] = useState('dashboard'); // dashboard, class_detail, subject_detail, eca_detail, committee_detail

  // Use custom hook for data fetching
  const { teachers, classes, classMembers, teacherAssignments, loading, error } = useTeacherData();

  // Set current teacher when data loads
  useEffect(() => {
    if (teachers.length > 0 && !currentTeacher) {
      setCurrentTeacher(teachers[0]);
    }
  }, [teachers, currentTeacher]);

  // Get the effective tenant ID - use hardcoded fallback if saasData.currentTenant is not available
  const getEffectiveTenantId = () => {
    // Try to get from saasData.currentTenant first
    if (saasData?.currentTenant?.id) {
      console.log('Using tenant ID from saasData:', saasData.currentTenant.id);
      return saasData.currentTenant.id;
    }
    
    // Fallback to hardcoded tenant ID for MyClass Real School A
    console.log('Using hardcoded tenant ID as fallback');
    return 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';
  };

  const effectiveTenantId = getEffectiveTenantId();

  // Handle tab change with source tracking
  const handleTabChangeWithSource = (tabName, sourceInfo) => {
    console.log('TeacherDirectoryModule: Navigating to', tabName, 'with source:', sourceInfo);
    onTabChange(tabName, sourceInfo);
  };

  // Main render
  return (
    <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
      {/* Navigation Header */}
      <div className="teacher-directory-header">
        <button className="back-button" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>教師通訊錄</h1>
        <div className="view-navigation">
          <button
            className={`nav-button ${selectedView === 'directory' ? 'active' : ''}`}
            onClick={() => setSelectedView('directory')}
          >
            教師列表
          </button>
          <button
            className={`nav-button ${selectedView === 'classroom-management' ? 'active' : ''}`}
            onClick={() => setSelectedView('classroom-management')}
          >
            課室管理
          </button>
          <button
            className={`nav-button ${selectedView === 'subject-teachers' ? 'active' : ''}`}
            onClick={() => setSelectedView('subject-teachers')}
          >
            按科目查看
          </button>
        </div>
      </div>

      {selectedView === 'directory' && (
        <TeacherDirectoryView
          teachers={teachers}
          classes={classes}
          classMembers={classMembers}
          loading={loading}
          error={error}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          departmentFilter={departmentFilter}
          setDepartmentFilter={setDepartmentFilter}
          onTeacherSelect={setSelectedTeacher}
        />
      )}
      {selectedView === 'classroom-management' && (
        selectedClassDetail ? (
          <ClassDetailPage
            classData={selectedClassDetail}
            onBack={() => setSelectedClassDetail(null)}
          />
        ) : (
          <ComprehensiveClassroomDashboard
            tenantId={effectiveTenantId}
            onClassClick={setSelectedClassDetail}
            onTabChange={handleTabChangeWithSource}
            onSidebarClose={onSidebarClose}
          />
        )
      )}
      {selectedView === 'subject-teachers' && (
        <SubjectTeachersView
          teachers={teachers}
          loading={loading}
          onTeacherSelect={setSelectedTeacher}
        />
      )}
      
      <TeacherDetailModal
        teacher={selectedTeacher}
        classes={classes}
        onClose={() => setSelectedTeacher(null)}
      />
    </div>
  );
};

export default TeacherDirectoryModule;
